/**
 * SkillBadges Component
 * Clean skill indicators with staggered slide-in animation
 */

import { useMemo } from 'react';
import {
  CalculatorOutlined,
  SearchOutlined,
  FileTextOutlined,
  DatabaseOutlined,
  ExportOutlined,
  BulbOutlined,
  ThunderboltOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons';
import './SkillBadges.css';
import { useLanguage } from '@/hooks/useLanguage';
import type { TFunc } from '@/utils/tfunc';

// Map skill names to icons
const SKILL_ICONS: Record<string, React.ReactNode> = {
  'calculator': <CalculatorOutlined />,
  'web_search': <SearchOutlined />,
  'file_analyzer': <FileTextOutlined />,
  'rag_query': <DatabaseOutlined />,
  'knowledge_search': <SearchOutlined />,
  'read_file': <FileTextOutlined />,
  'data_export': <ExportOutlined />,
  'palindrome': <BulbOutlined />,
  'trivia': <QuestionCircleOutlined />,
  'trivia questions generator': <QuestionCircleOutlined />,
};

// Internal tools — these get a distinct visual style
const INTERNAL_TOOLS = new Set(['knowledge_search', 'read_file']);

// Friendly display names for internal tools
const TOOL_DISPLAY_KEYS: Record<string, string> = {
  'knowledge_search': 'chat.skillBadge.searchingKnowledge',
  'read_file': 'chat.skillBadge.readingFile',
};

const isInternalTool = (skillName: string): boolean => {
  return INTERNAL_TOOLS.has(skillName.toLowerCase().replace(/\s+/g, '_'));
};

// Get icon for a skill name (with fallback)
const getSkillIcon = (skillName: string): React.ReactNode => {
  const normalizedName = skillName.toLowerCase().replace(/\s+/g, '_');

  if (SKILL_ICONS[normalizedName]) {
    return SKILL_ICONS[normalizedName];
  }

  for (const [key, icon] of Object.entries(SKILL_ICONS)) {
    if (normalizedName.includes(key) || key.includes(normalizedName)) {
      return icon;
    }
  }

  return <ThunderboltOutlined />;
};

// Format skill name for display — also the review window's (Insights → Feedback)
export const formatSkillName = (t: TFunc, skillName: string): string => {
  // Check for friendly tool display names first
  const normalized = skillName.toLowerCase().replace(/\s+/g, '_');
  if (TOOL_DISPLAY_KEYS[normalized]) {
    return t(TOOL_DISPLAY_KEYS[normalized]);
  }

  return skillName
    .replace(/_/g, ' ')
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

interface SkillBadgesProps {
  skills: string[];
  animated?: boolean;
}

export function SkillBadges({ skills, animated = true }: SkillBadgesProps) {
  const { t } = useLanguage();
  const uniqueSkills = useMemo(() => {
    return [...new Set(skills.filter(s => s && s.trim()))];
  }, [skills]);

  if (uniqueSkills.length === 0) {
    return null;
  }

  return (
    <div className={`skill-badges-container ${animated ? '' : 'no-animation'}`}>
      {uniqueSkills.map((skill, index) => {
        const isTool = isInternalTool(skill);
        return (
          <div
            key={`${skill}-${index}`}
            className={`skill-badge ${isTool ? 'skill-badge--tool' : ''}`}
            style={animated ? { '--animation-delay': `${index * 100}ms` } as React.CSSProperties : undefined}
          >
            <span className="skill-badge-icon">{getSkillIcon(skill)}</span>
            <span className="skill-badge-name">{formatSkillName(t, skill)}</span>
            {isTool && animated && <span className="skill-badge-activity" />}
          </div>
        );
      })}
    </div>
  );
}
